import GapFillIcon from "@/components/icons/exerciseV2/GapFillIcon";
import { ExerciseOptionTypes, Submission } from "@/types";
import Button from "@edusogno/primitives/button.js";
import { FC, useEffect, useState } from "react";
import {
  characterSpecialForSplit,
  checkCharacterSpecialForSplit,
} from "../Desktop/GapFillExercise";
import GrammarExerciseResult from "./GrammarExerciseResult";

interface QuestionProps {
  answers: Answers;
  question: string;
  onAnswer: (index: number, answer: string) => void;
  showCorrection?: boolean;
  correctionData?: {
    [key: number]: {
      studentAnswer: string;
      correctAnswer: string;
      isCorrect: boolean;
    };
  };
  currentQuestionIndex: number;
}

interface CorrectionFeedbackProps {
  question: string;
  correctionData: {
    [key: number]: {
      studentAnswer: string;
      correctAnswer: string;
      isCorrect: boolean;
    };
  };
  currentQuestionIndex: number;
}

interface QuestionData {
  id: number;
  statement: string;
  correct_answer: string;
  questionWithBlanks?: string;
}

interface GapFillExerciseProps {
  questions: QuestionData[];
}

interface CorrectionData {
  correctAnswer: string;
  isCorrect: boolean;
  studentAnswer: string;
}

interface PreparedData {
  [key: string]: Submission[];
}

interface Answers {
  [key: number]: string;
}

const CorrectionFeedback: FC<CorrectionFeedbackProps> = ({
  question,
  correctionData,
  currentQuestionIndex,
}) => {
  let placeholderCount = 0;

  return (
    <div className="flex gap-1 items-center flex-wrap px-4 py-3 w-full text-black text-[10px] font-medium border-black border-[1px] rounded-[10px]">
      <p className="text-sm font-semibold ">{currentQuestionIndex + 1} .</p>

      {question.split(" ").map((word, index) => {
        if (checkCharacterSpecialForSplit(word)) {
          const correction = correctionData[placeholderCount];
          const correctAnswer = correction?.correctAnswer.split("|");
          const isCorrect = correction ? correction.isCorrect : null;

          placeholderCount++;

          return (
            <span
              key={index}
              className={`flex items-center justify-center rounded min-w-[60px] h-[20px] font-medium text-center text-black text-[10px] ${
                isCorrect ? "bg-[#D3EADD]" : "bg-[#FE9595]"
              }`}
            >
              {correctAnswer.find((a) => {
                const ca = a.trim().toLowerCase();
                const sa = correction?.studentAnswer.trim().toLowerCase();
                return ca.charAt(0) == sa.charAt(0) && ca.includes(sa);
              }) ??
                correctAnswer[0] ??
                "Errore"}
            </span>
          );
        }

        return (
          <span key={index} className="my-auto">
            {word}
          </span>
        );
      })}
    </div>
  );
};

const Question: FC<QuestionProps> = ({
  answers,
  question,
  onAnswer,
  showCorrection,
  currentQuestionIndex,
}) => {
  return (
    <div className="flex flex-col">
      <div className="flex gap-1 items-center flex-wrap px-4 py-3 w-full bg-light-400 rounded-md text-black font-medium text-[10px]">
        <p className="text-sm font-semibold ">{currentQuestionIndex + 1} .</p>

        {question.split(" ").map((word, index) => {
          if (!checkCharacterSpecialForSplit(word)) {
            return (
              <span key={index} className="my-auto font-medium">
                {word}
              </span>
            );
          }

          return (
            <input
              key={index}
              type="text"
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                target.style.width = `${target.scrollWidth}px`;
              }}
              value={answers[index] ?? ""}
              readOnly={showCorrection}
              onChange={(e) => onAnswer(index, e.target.value)}
              className={`bg-white text-center text-black font-medium text-[10px] rounded p-1 w-[60px] h-[20px]`}
              placeholder="___"
            />
          );
        })}
      </div>
    </div>
  );
};

const GapFillExerciseMobile: FC<GapFillExerciseProps> = ({ questions }) => {
  const totalQuestions = questions.length;

  const [answers, setAnswers] = useState<Answers>({});
  const [showResults, setShowResults] = useState(false);
  const [preparedData, setPreparedData] = useState<PreparedData>({});
  const [showCorrection, setShowCorrection] = useState<boolean>(false);
  const [correctionData, setCorrectionData] = useState<CorrectionData[]>([]);
  const [preparedQuestions, setPreparedQuestions] = useState<QuestionData[]>(
    [],
  );
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);

  const currentQuestion = preparedQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  const handleNextQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
    setAnswers({});
    setShowCorrection(false);
  };

  const handleAnswer = (index: number, answer: string) => {
    setAnswers((p) => ({
      ...p,
      [index]: answer.trim(),
    }));
  };

  const handleShowCorrection = () => {
    if (!currentQuestion || !currentQuestion.questionWithBlanks) {
      console.error("Current question or question with blanks is undefined.");
      return;
    }
    const blanksIndices: number[] = [];

    currentQuestion.questionWithBlanks.split(" ").forEach((word, index) => {
      if (checkCharacterSpecialForSplit(word)) {
        blanksIndices.push(index);
      }
    });

    const correctAnswersArray = currentQuestion.correct_answer
      .split(":")
      .map((answer) => answer.trim());

    const correctedAnswers = blanksIndices.map((b, i) => {
      const studentAnswer = answers[b] || "";
      const correctAnswer = correctAnswersArray[i];
      const isCorrect = correctAnswer
        .split("|")
        .some(
          (ca) => ca.trim().toLowerCase() == studentAnswer.trim().toLowerCase(),
        );

      return { studentAnswer, correctAnswer, isCorrect };
    });

    setCorrectionData(correctedAnswers);
    setShowCorrection(true);

    const correctAnswersForCurrentQuestion =
      correctedAnswers.filter((a) => a.isCorrect).length /
      correctedAnswers.length;

    setCorrectAnswersCount((p) => p + correctAnswersForCurrentQuestion);
  };

  useEffect(() => {
    const prepareQuestions = questions.map((question) => {
      const keyWords = question.correct_answer
        .split(":")
        .map((answer) => answer.trim());

      let questionWithBlanks = question.statement;

      keyWords.forEach((word) => {
        questionWithBlanks = questionWithBlanks.replaceAll(
          " " + word + " ",
          " " + characterSpecialForSplit + " ",
        );
      });

      return { ...question, questionWithBlanks };
    });

    setPreparedQuestions(prepareQuestions);
  }, [questions]);

  useEffect(() => {
    if (correctionData.length > 0 && currentQuestion) {
      const submissions = correctionData.map((data) => ({
        questionId: currentQuestion.id,
        textAnswer: data.studentAnswer,
      }));

      setPreparedData((p) => ({
        ...p,
        [currentQuestionIndex]: submissions,
      }));
    }
  }, [correctionData, currentQuestion, currentQuestionIndex]);

  useEffect(() => {
    // Blocca lo scroll
    document.body.style.overflowY = "hidden";

    // Riabilita lo scroll quando il componente viene smontato
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  const handleShowResults = () => {
    // const submissionData = Object.values(preparedData).flat();
    setShowResults(true);
  };

  if (showResults) {
    return (
      <GrammarExerciseResult
        onClose={() => setShowResults(false)}
        data={{
          exerciseType: ExerciseOptionTypes.GapFill,
          score: Number(
            Number.isInteger(correctAnswersCount)
              ? correctAnswersCount
              : correctAnswersCount.toFixed(2),
          ),
          total: totalQuestions,
          submissionData: Object.values(preparedData).flat(),
        }}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-2 px-5 pt-5 bg-white rounded-lg border border-brand-secondary border-solid">
        <header className="flex gap-5 justify-between w-full max-md:flex-wrap max-md:max-w-full">
          <div className="flex gap-2 justify-between items-center text-xl leading-3 text-black">
            <GapFillIcon className="md:w-4 md:h-4" />

            <div className="w-full ">
              <p className="text-xs font-bold ">Gap fill</p>

              <p className="text-[10px] font-normal text-black">
                Choose the correct question
              </p>
            </div>
          </div>
        </header>

        <div className="flex flex-col gap-11">
          <section className="flex flex-col gap-2 text-sm leading-4 min-h-[200px]">
            {currentQuestion && (
              <Question
                answers={answers}
                question={currentQuestion?.questionWithBlanks || ""}
                onAnswer={(index, answer) => handleAnswer(index, answer)}
                showCorrection={showCorrection}
                correctionData={correctionData}
                currentQuestionIndex={currentQuestionIndex}
              />
            )}

            {showCorrection && (
              <CorrectionFeedback
                question={currentQuestion?.questionWithBlanks || ""}
                correctionData={correctionData}
                currentQuestionIndex={currentQuestionIndex}
              />
            )}
          </section>
        </div>
      </div>

      {!showCorrection && (
        <Button
          className="mx-auto mt-4 text-[10px] rounded-[10px] w-full border-black bg-white"
          variant={"outlined"}
          onClick={handleShowCorrection}
          disabled={Object.keys(answers).length === 0 || showCorrection}
        >
          Correct now
        </Button>
      )}

      {showCorrection && (
        <Button
          variant="contained"
          className={`mx-auto mt-4 text-[10px] w-full rounded-[10px] ${
            !showCorrection ? "bg-white hover:bg-white text-black" : ""
          } ${
            isLastQuestion
              ? "bg-brand-secondary hover:bg-brand-secondary text-white"
              : ""
          } `}
          onClick={isLastQuestion ? handleShowResults : handleNextQuestion}
        >
          {isLastQuestion ? "My Score ⭐" : "Next question"}
        </Button>
      )}
    </>
  );
};

export default GapFillExerciseMobile;
